import { TableColumns } from "../components/table-columns";
import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { api, type KeyMetadata, type UserRecord } from "../api/client";
import { i18n } from "../i18n";
import { dateTime, readable } from "../view-model";
import "../components/app-dialog";
import { clampPage, pageCount, pageItems } from "../components/app-pagination";
import {
  nextTableSort,
  sortableTableHeader,
  sortTableItems,
  type TableSort,
} from "../components/table-sort";
import { waitForLoadingCycle } from "../components/table-loading";

const AVAILABLE_SCOPES = [
  "knowledge:read",
  "knowledge:write",
  "rag:read",
  "source:read",
  "source:refresh",
  "codex:execute",
  "codex:unrestricted",
  "codex:local-config",
  "*",
];
const DEFAULT_SCOPES = ["knowledge:write", "rag:read", "codex:execute"];
const ROLES = ["administrator", "operator", "viewer"] as const;

@customElement("keys-view")
export class KeysView extends LitElement {
  private readonly columns = new TableColumns(this);
  @state() private keys: KeyMetadata[] = [];
  @state() private page = 1;
  @state() private name = "mcp-client";
  @state() private scopes = [...DEFAULT_SCOPES];
  @state() private revokeTarget: KeyMetadata | null = null;
  @state() private createOpen = false;
  @state() private busy = false;
  @state() private loading = true;
  @state() private error = "";
  @state() private dialogError = "";
  @state() private success = "";
  @state() private createdKey = "";
  @state() private sort: TableSort = { key: "created_at", direction: "desc" };

  private readonly languageChanged = () => this.requestUpdate();

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    void this.load();
  }

  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }

  private async load() {
    const loadingStarted = performance.now();
    this.loading = true;
    this.error = "";
    try {
      this.keys = (await api.keys()).items;
      this.page = clampPage(this.page, this.keys.length);
    } catch (error) {
      this.error = (error as Error).message;
    } finally {
      await waitForLoadingCycle(loadingStarted);
      this.loading = false;
    }
  }

  private openCreate() {
    this.name = "mcp-client";
    this.scopes = [...DEFAULT_SCOPES];
    this.dialogError = "";
    this.createOpen = true;
  }

  private toggleScope(scope: string, checked: boolean) {
    this.scopes = checked
      ? [...new Set([...this.scopes, scope])]
      : this.scopes.filter((value) => value !== scope);
  }

  private async create() {
    if (!this.name.trim() || this.scopes.length === 0) {
      this.dialogError = i18n.t("formFieldsRequired");
      return;
    }
    this.busy = true;
    this.dialogError = "";
    try {
      const created = await api.createKey(this.name.trim(), this.scopes);
      this.createdKey = created.key;
      sessionStorage.setItem("apiKey", created.key);
      this.success = i18n.t("keysCreatedStatus");
      this.createOpen = false;
      await this.load();
    } catch (error) {
      this.dialogError = (error as Error).message;
    } finally {
      this.busy = false;
    }
  }

  private async revoke() {
    if (!this.revokeTarget) return;
    this.busy = true;
    this.dialogError = "";
    try {
      const deleting = Boolean(this.revokeTarget.revoked_at);
      if (deleting) await api.deleteKeyRecord(this.revokeTarget.id);
      else await api.deleteKey(this.revokeTarget.id);
      this.success = i18n.t(
        deleting ? "keysDeletedStatus" : "keysRevokedStatus",
      );
      this.revokeTarget = null;
      await this.load();
    } catch (error) {
      this.dialogError = (error as Error).message;
    } finally {
      this.busy = false;
    }
  }

  private get totalPages() {
    return pageCount(this.keys.length);
  }

  private get visibleKeys() {
    return pageItems(
      sortTableItems(this.keys, this.sort, (item, key) => item[key]),
      this.page,
    );
  }

  private setSort(key: string) {
    this.sort = nextTableSort(this.sort, key);
    this.page = 1;
  }

  private sortHeader(label: string, key: string) {
    return sortableTableHeader(label, key, this.sort, (value) =>
      this.setSort(value),
    );
  }

  render() {
    return html`<page-heading
        heading=${i18n.t("keysTitle")}
        description=${i18n.t("keysDescription")}
      >
        <a2k-button @click=${this.openCreate}>
          ${i18n.t("keysGenerate")}
        </a2k-button>
      </page-heading>
      ${
        this.createdKey
          ? html`<surface-window heading=${i18n.t("keysCopySecret")}>
              <pre class="code-output">${this.createdKey}</pre>
            </surface-window>`
          : null
      }
      ${
        this.error
          ? html`<p class="status status-error">${this.error}</p>`
          : null
      }
      ${
        this.success
          ? html`<p class="status status-success">${this.success}</p>`
          : null
      }
      <surface-window heading=${i18n.t("keysInventory")}>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                ${this.sortHeader(i18n.t("keysName"), "name")}
                ${this.sortHeader(i18n.t("keysPrefix"), "prefix")}
                <th>${i18n.t("keysScopes")}</th>
                ${this.sortHeader(i18n.t("keysCreated"), "created_at")}
                ${this.sortHeader(i18n.t("keysLastUsed"), "last_used_at")}
                ${this.sortHeader(i18n.t("state"), "revoked_at")}
                <th>${i18n.t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              ${
                this.loading
                  ? html`<tr>
                      <td colspan="7">
                        <table-loading
                          label=${i18n.t("loading")}
                        ></table-loading>
                      </td>
                    </tr>`
                  : this.visibleKeys.length
                    ? this.visibleKeys.map(
                        (key) =>
                          html`<tr>
                            <td>${key.name}</td>
                            <td>${key.prefix || "-"}</td>
                            <td>${readable(key.scopes)}</td>
                            <td>${dateTime(key.created_at)}</td>
                            <td>${dateTime(key.last_used_at)}</td>
                            <td>
                              ${
                                key.revoked_at
                                  ? i18n.t("disabled")
                                  : i18n.t("active")
                              }
                            </td>
                            <td>
                              <a2k-button
                                ?disabled=${this.busy}
                                @click=${() => {
                                  this.dialogError = "";
                                  this.revokeTarget = key;
                                }}
                              >
                                ${i18n.t(key.revoked_at ? "keysDeleteRecord" : "keysRevoke")}
                              </a2k-button>
                            </td>
                          </tr>`,
                      )
                    : html`<tr>
                        <td class="empty-cell" colspan="7">
                          ${i18n.t("keysEmpty")}
                        </td>
                      </tr>`
              }
                    </tbody>
                  </table>
                </div>
              </surface-window>
              <app-pagination
                .page=${this.page}
                .totalPages=${this.totalPages}
                .busy=${this.loading}
                @page-change=${(event: CustomEvent<{ page: number }>) => {
                  this.page = event.detail.page;
                }}
              ></app-pagination>
      </div>
      <app-dialog
        .open=${this.createOpen}
        heading=${i18n.t("keysCreateTitle")}
        confirm-label=${i18n.t("keysGenerate")}
        cancel-label=${i18n.t("cancel")}
        working-label=${i18n.t("working")}
        .busy=${this.busy}
        .message=${this.dialogError}
        @dialog-cancel=${() => (this.createOpen = false)}
        @dialog-confirm=${this.create}
      >
        <label class="form-field">
          <span class="field-label">${i18n.t("keysName")}</span>
          <input
            class="a2k-native-input"
            .value=${this.name}
            @input=${(event: Event) => {
              this.name = (event.target as HTMLInputElement).value;
            }}
          />
        </label>
        <fieldset class="checkbox-grid">
          <legend>${i18n.t("keysScopes")}</legend>
          ${AVAILABLE_SCOPES.map(
            (scope) =>
              html`<label class="checkbox-field">
                <input
                  type="checkbox"
                  .checked=${this.scopes.includes(scope)}
                  @change=${(event: Event) =>
                    this.toggleScope(
                      scope,
                      (event.target as HTMLInputElement).checked,
                    )}
                />
                <span
                  >${scope}${
                    scope === "codex:local-config"
                      ? html`<small>${i18n.t("codexLocalScopeHelp")}</small>`
                      : null
                  }</span
                >
              </label>`,
          )}
        </fieldset>
      </app-dialog>
      <app-dialog
        .open=${this.revokeTarget !== null}
        heading=${i18n.t(this.revokeTarget?.revoked_at ? "keysDeleteRecord" : "keysRevoke")}
        confirm-label=${i18n.t(this.revokeTarget?.revoked_at ? "keysDeleteRecord" : "keysRevoke")}
        cancel-label=${i18n.t("cancel")}
        working-label=${i18n.t("working")}
        .busy=${this.busy}
        .message=${this.dialogError}
        @dialog-cancel=${() => (this.revokeTarget = null)}
        @dialog-confirm=${this.revoke}
      >
        <a2k-panel>${i18n.t(this.revokeTarget?.revoked_at ? "keysConfirmDelete" : "keysConfirmRevoke")}</a2k-panel>
      </app-dialog>`;
  }
}

type UserEditorMode = "create" | "edit" | "delete" | null;

@customElement("users-view")
export class UsersView extends LitElement {
  private readonly columns = new TableColumns(this);
  @state() private users: UserRecord[] = [];
  @state() private page = 1;
  @state() private mode: UserEditorMode = null;
  @state() private target: UserRecord | null = null;
  @state() private username = "";
  @state() private email = "";
  @state() private password = "";
  @state() private selectedRole: UserRecord["role"] = "viewer";
  @state() private isActive = true;
  @state() private loading = true;
  @state() private busy = false;
  @state() private error = "";
  @state() private dialogError = "";
  @state() private success = "";
  @state() private sort: TableSort = { key: "username", direction: "asc" };

  private readonly languageChanged = () => this.requestUpdate();

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    void this.load();
  }

  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }

  private async load() {
    const loadingStarted = performance.now();
    this.loading = true;
    this.error = "";
    try {
      this.users = (await api.users()).items;
      this.page = clampPage(this.page, this.users.length);
    } catch (error) {
      this.error = (error as Error).message;
    } finally {
      await waitForLoadingCycle(loadingStarted);
      this.loading = false;
    }
  }

  private openCreate() {
    this.mode = "create";
    this.target = null;
    this.username = "";
    this.email = "";
    this.password = "";
    this.selectedRole = "viewer";
    this.isActive = true;
    this.dialogError = "";
  }

  private openEdit(user: UserRecord) {
    this.mode = "edit";
    this.target = user;
    this.username = user.username;
    this.email = user.email || "";
    this.password = "";
    this.selectedRole = user.role;
    this.isActive = user.is_active;
    this.dialogError = "";
  }

  private closeDialog() {
    if (this.busy) return;
    this.mode = null;
    this.target = null;
    this.dialogError = "";
  }

  private async saveUser() {
    if (!this.username.trim()) {
      this.dialogError = i18n.t("formFieldsRequired");
      return;
    }
    if (this.mode === "create" && this.password.length < 8) {
      this.dialogError = i18n.t("formFieldsRequired");
      return;
    }
    if (this.password && this.password.length < 8) {
      this.dialogError = i18n.t("formFieldsRequired");
      return;
    }
    this.busy = true;
    this.dialogError = "";
    try {
      const values: Record<string, unknown> = {
        username: this.username.trim(),
        email: this.email.trim(),
        role: this.selectedRole,
        is_active: this.isActive,
      };
      if (this.password) values.password = this.password;
      if (this.mode === "create") {
        await api.createUser(values);
        this.success = i18n.t("usersCreatedStatus");
      } else if (this.target) {
        await api.updateUser(this.target.id, values);
        this.success = i18n.t("usersUpdatedStatus");
      }
      this.mode = null;
      this.target = null;
      await this.load();
    } catch (error) {
      this.dialogError = (error as Error).message;
    } finally {
      this.busy = false;
    }
  }

  private async deleteUser() {
    if (!this.target) return;
    this.busy = true;
    this.dialogError = "";
    try {
      await api.deleteUser(this.target.id);
      this.success = i18n.t("usersDeletedStatus");
      this.mode = null;
      this.target = null;
      await this.load();
    } catch (error) {
      this.dialogError = (error as Error).message;
    } finally {
      this.busy = false;
    }
  }

  private get totalPages() {
    return pageCount(this.users.length);
  }

  private get visibleUsers() {
    return pageItems(
      sortTableItems(this.users, this.sort, (item, key) => item[key]),
      this.page,
    );
  }

  private setSort(key: string) {
    this.sort = nextTableSort(this.sort, key);
    this.page = 1;
  }

  private sortHeader(label: string, key: string) {
    return sortableTableHeader(label, key, this.sort, (value) =>
      this.setSort(value),
    );
  }

  render() {
    const deleting = this.mode === "delete";
    return html`<page-heading
        heading=${i18n.t("usersTitle")}
        description=${i18n.t("usersDescription")}
      >
        <a2k-button @click=${this.openCreate}>${i18n.t("usersAdd")}</a2k-button>
      </page-heading>
      ${
        this.error
          ? html`<p class="status status-error">${this.error}</p>`
          : null
      }
      ${
        this.success
          ? html`<p class="status status-success">${this.success}</p>`
          : null
      }
      <surface-window heading=${i18n.t("usersDirectory")}>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                ${this.sortHeader(i18n.t("usersUser"), "username")}
                ${this.sortHeader(i18n.t("usersRole"), "role")}
                ${this.sortHeader(i18n.t("state"), "is_active")}
                ${this.sortHeader(i18n.t("usersLastLogin"), "last_login")}
                <th>${i18n.t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              ${
                this.loading
                  ? html`<tr>
                      <td colspan="5">
                        <table-loading
                          label=${i18n.t("loading")}
                        ></table-loading>
                      </td>
                    </tr>`
                  : this.visibleUsers.length
                    ? this.visibleUsers.map(
                        (user) =>
                          html`<tr>
                            <td>${user.username}</td>
                            <td>${user.role}</td>
                            <td>
                              ${
                                user.is_active
                                  ? i18n.t("active")
                                  : i18n.t("disabled")
                              }
                            </td>
                            <td>${dateTime(user.last_login)}</td>
                            <td>
                              <div class="resource-actions">
                                <a2k-button @click=${() => this.openEdit(user)}>
                                  ${i18n.t("edit")}
                                </a2k-button>
                                ${
                                  !user.is_active
                                    ? html`<a2k-button
                                        @click=${async () => {
                                          try {
                                            await api.updateUser(user.id, {
                                              is_active: true,
                                            });
                                            this.success = i18n.t(
                                              "usersApprovedStatus",
                                            );
                                            await this.load();
                                          } catch (error) {
                                            this.error = (
                                              error as Error
                                            ).message;
                                          }
                                        }}
                                        >${i18n.t("usersApprove")}</a2k-button
                                      >`
                                    : null
                                }
                                <a2k-button
                                  @click=${() => {
                                    this.target = user;
                                    this.mode = "delete";
                                    this.dialogError = "";
                                  }}
                                >
                                  ${i18n.t("remove")}
                                </a2k-button>
                              </div>
                            </td>
                          </tr>`,
                      )
                    : html`<tr>
                        <td class="empty-cell" colspan="5">
                          ${i18n.t("usersEmpty")}
                        </td>
                      </tr>`
              }
                    </tbody>
                  </table>
                </div>
              </surface-window>
              <app-pagination
                .page=${this.page}
                .totalPages=${this.totalPages}
                .busy=${this.loading}
                @page-change=${(event: CustomEvent<{ page: number }>) => {
                  this.page = event.detail.page;
                }}
              ></app-pagination>
      </div>
      <app-dialog
        .open=${this.mode !== null}
        heading=${
          deleting
            ? i18n.t("usersDelete")
            : this.mode === "edit"
              ? i18n.t("usersEditTitle")
              : i18n.t("usersCreateTitle")
        }
        confirm-label=${deleting ? i18n.t("remove") : i18n.t("save")}
        cancel-label=${i18n.t("cancel")}
        working-label=${i18n.t("working")}
        .busy=${this.busy}
        .message=${this.dialogError}
        @dialog-cancel=${this.closeDialog}
        @dialog-confirm=${deleting ? this.deleteUser : this.saveUser}
      >
        ${
          deleting
            ? html`<a2k-panel>
                ${i18n.t("usersConfirmDelete", {
                  username: this.target?.username || "",
                })}
              </a2k-panel>`
            : html`<div class="form-grid">
                <label class="form-field">
                  <span class="field-label">${i18n.t("usersUser")}</span>
                  <input
                    class="a2k-native-input"
                    .value=${this.username}
                    @input=${(event: Event) => {
                      this.username = (event.target as HTMLInputElement).value;
                    }}
                  />
                </label>
                <label class="form-field">
                  <span class="field-label">${i18n.t("registerEmail")}</span>
                  <input
                    class="a2k-native-input"
                    type="email"
                    .value=${this.email}
                    @input=${(event: Event) => {
                      this.email = (event.target as HTMLInputElement).value;
                    }}
                  />
                </label>
                <label class="form-field">
                  <span class="field-label">${i18n.t("usersRole")}</span>
                  <select
                    class="a2k-native-input"
                    .value=${this.selectedRole}
                    @change=${(event: Event) => {
                      this.selectedRole = (event.target as HTMLSelectElement)
                        .value as UserRecord["role"];
                    }}
                  >
                    ${ROLES.map(
                      (role) => html`<option value=${role}>${role}</option>`,
                    )}
                  </select>
                </label>
                <label class="form-field">
                  <span class="field-label">
                    ${
                      this.mode === "edit"
                        ? i18n.t("usersNewPassword")
                        : i18n.t("usersPassword")
                    }
                  </span>
                  <input
                    class="a2k-native-input"
                    type="password"
                    aria-describedby=${
                      this.mode === "edit" ? "user-password-help" : undefined
                    }
                    .value=${this.password}
                    @input=${(event: Event) => {
                      this.password = (event.target as HTMLInputElement).value;
                    }}
                  />
                  ${
                    this.mode === "edit"
                      ? html`<small id="user-password-help" class="field-help"
                          >${i18n.t("usersPasswordOptional")}</small
                        >`
                      : null
                  }
                </label>
                <label class="checkbox-field">
                  <input
                    type="checkbox"
                    .checked=${this.isActive}
                    @change=${(event: Event) => {
                      this.isActive = (
                        event.target as HTMLInputElement
                      ).checked;
                    }}
                  />
                  <span>${i18n.t("active")}</span>
                </label>
              </div>`
        }
      </app-dialog>`;
  }
}
